<template>
  <page title="Billing Details" :loading="loading" wide backURI=":back">
    <template #controls>
      <div class="billing-report-detail-controls">
        <div class="billing-page-search-controls">
          <form-button v-show="!pageSearchOpen" class="billing-page-search-toggle"
                       :aria-expanded="pageSearchOpen"
                       @click="togglePageSearch">
            <i class="fal fa-search"></i>
          </form-button>
          <div class="billing-page-search-expand" :class="{ 'is-open': pageSearchOpen }"
               :aria-hidden="!pageSearchOpen">
            <div class="billing-page-search-expand-inner">
              <div class="billing-page-search-field-wrap">
                <text-input ref="pageSearchInput" class="billing-page-search-input"
                            placeholder="Type to find…"
                            @input.native="onPageSearchInput"
                            @keydown.native="onPageSearchKeydown" />
                <button type="button" class="billing-page-search-close" aria-label="Close search"
                        @click="closePageSearch">
                  <i class="fal fa-times"></i>
                </button>
              </div>
              <div v-if="pageSearchQuery.trim() && pageSearchMatchCount > 0" class="billing-page-search-meta">
                <span>{{ pageSearchMatchIndex + 1 }} / {{ pageSearchMatchCount }}</span>
                <button type="button" class="billing-page-search-nav" title="Previous (Shift+Enter)"
                        @click="pageSearchStep(-1)">
                  <i class="fal fa-chevron-up"></i>
                </button>
                <button type="button" class="billing-page-search-nav" title="Next (Enter)"
                        @click="pageSearchStep(1)">
                  <i class="fal fa-chevron-down"></i>
                </button>
              </div>
              <div v-else-if="pageSearchQuery.trim() && pageSearchMatchCount === 0"
                   class="billing-page-search-meta">
                No matches
              </div>
              <div v-if="pageSearchSearching" class="billing-page-search-meta">
                Searching...
              </div>
            </div>
          </div>
        </div>
        <form-button @click="exportCsv">Export to Excel</form-button>
      </div>
    </template>

    <template #body>
      <div v-if="bill" ref="billingPageSearchRoot" class="billing-page-search-root">
        <div class="form">
          <div class="form-row">
            <div class="form-item" data-page-search-form="account">
              <div class="label">Account</div>
              <div class="value">{{current.account.name}}</div>
            </div>
            <div class="form-item" data-page-search-form="system">
              <div class="label">System</div>
              <div class="value">{{current.account.system.name}}</div>
            </div>
            <div class="form-item" data-page-search-form="dueDate">
              <div class="label">Month</div>
              <div class="value">{{extractMonthOfReference(bill.dueDate)}}</div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-item" data-page-search-form="billableCount">
              <div class="label">Billable Count</div>
              <div class="value">{{bill.totals.count.active}}</div>
            </div>
            <div class="form-item" data-page-search-form="holdingPausedCount">
              <div class="label">Holding/Paused Count</div>
              <div class="value">{{bill.totals.count.holding + bill.totals.count.paused}}</div>
            </div>
            <div class="form-item" data-page-search-form="billableTotal">
              <div class="label">Billable Total</div>
              <div class="value ">{{bill.totals.total.active}} {{current.currency}}</div>
            </div>
          </div>
        </div>

        <div v-for="(group, groupIndex) in bill.groups" :key="group.rate.id" class="billing-section">
          <div v-if="group.rate.description !== 'Default'" class="billing-section-title"
               :data-page-search-section="groupIndex">{{ group.rate.description }}</div>

          <div class="billing-section-content">
            <data-table :value="group.devices" :refresh="false" :paginator="false" :rows="1000">
              <column bodyStyle="text-align: center;" field="id" header="IMEI" sortable>
                <template #body="slot">
                  <span :data-page-search-device="groupIndex + ':' + slot.data.id">
                    {{ slot.data.id }}
                  </span>
                </template>
              </column>
              <column bodyStyle="text-align: center;" field="name" header="Vehicle ID" sortable/>
              <column bodyStyle="text-align: center;" field="type" header="Type" sortable/>
              <column bodyStyle="text-align: center;" field="fleet" header="Fleet" sortable/>
              <column bodyStyle="text-align: center;" field="carrier" header="Carrier" sortable>
                <template #body="slot">
                  <span>
                    {{carriersMap[slot.data.carrier]}}
                  </span>
                </template>
              </column>
              <column bodyStyle="text-align: center;" field="activation.status" header="Status" sortable>
                <template #body="slot">
                  <span class="badge" :class="slot.data.activation.status">
                    {{activationStatusMap[slot.data.activation.status]}}<span v-if="slot.data.activation.forced">*</span>
                  </span>
                </template>
              </column>
              <column bodyStyle="text-align: center;" field="activate.status.forced" header="Rate" sortable>
                <template #body="slot">
                  <span class="rate" :class="slot.data.activation.status">
                    {{group.rate.rate}} {{current.currency}}
                  </span>
                </template>
              </column>
            </data-table>
          </div>

          <div class="total" :data-page-search-total="groupIndex">
            <span>COUNT <strong>{{group.totals.count.count}}</strong></span>
            <span class="sep"></span>
            <span>BILLABLE <strong>{{group.totals.count.active}}</strong></span>
            <span class="sep"></span>
            <span>HOLDING <strong>{{group.totals.count.holding}}</strong></span>
            <span class="sep"></span>
            <span>PAUSED <strong>{{group.totals.count.paused}}</strong></span>
            <span class="sep"></span>
            <span>TOTAL <strong>{{group.totals.total.active}} {{current.currency}}</strong>/{{group.cycle.cycle}}</span>
          </div>
        </div>
      </div>

      <modal title="Export to Excel" ref="exportModal">
        <div class="export-modal-body">
          <p class="export-modal-hint">Enter the e-mail that should receive this report.</p>
          <text-input
            ref="exportEmailInput"
            type="email"
            label="E-mail"
            placeholder="Type an e-mail"
            :invalid="exportEmailError"
            @input.native="onExportEmailInput"
            @keyup.native.enter="requestManualExport"
          />
        </div>
        <template #footer>
          <button class="btn" type="button" @click="closeExportModal">Cancel</button>
          <button class="btn primary" type="button" :disabled="exportLoading" @click="requestManualExport">
            Request Manual
          </button>
        </template>
      </modal>
    </template>
  </page>

</template>

<script>
import DataTable from '@/components/DataTable.vue'
import FormButton from '@/components/FormButton.vue'
import Modal from '@/components/Modal.vue'
import Page from '@/components/Page.vue'
import TextInput from '@/components/TextInput.vue'

import Column from 'primevue/column'

import gql from 'graphql-tag'

import { syncWorkingAccount } from '@/lib/billing-navigation'

export default {
  name: 'BillingReportDetailView',

  components: {
    Page,
    DataTable,
    Column,
    FormButton,
    TextInput,
    Modal
  },

  methods: {
    getBill () {
      this.$apollo.query({
        query: gql`
          query ($systemId: String!, $accountId: ID!) {
            billing {
              current: getCurrentBillingBill(systemId: $systemId, accountId: $accountId) {
                billingAccountId
                currency
                account {
                  name
                  system {
                    name
                  }
                }
                rates {
                  normal {
                    dueDate
                    totals {
                      count {
                        active
                        holding
                        paused
                      }
                      total {
                        active
                      }
                    }
                    groups {
                      totals {
                        total {
                          active
                        }
                        count {
                          count
                          active
                          holding
                          paused
                        }
                      }
                      devices {
                        id
                        name
                        type
                        fleet
                        carrier
                        activation {
                          status
                          forced
                        }
                      }
                      cycle {
                        cycle
                      }
                      rate {
                        id
                        description
                        rate
                      }
                    }
                  }
                }
              }
            }
          }
        `,
        variables: {
          systemId: this.$route.params.systemId,
          accountId: this.$route.params.accountId
        }
      }).then(response => {
        this.current = response.data.billing.current
        syncWorkingAccount(this.$store, {
          value: this.$route.params.accountId,
          legacyAccountId: this.$route.params.accountId,
          label: this.current.account?.name,
          billingAccountId: this.current.billingAccountId,
          system: {
            id: this.$route.params.systemId,
            name: this.current.account?.system?.name
          }
        })
        this.$nextTick(() => {
          this.rebuildPageSearchIndex()
          if (this.pageSearchQuery.trim()) this.queuePageSearch()
        })
      }).finally(() => {
        this.loading = false
      })
    },

    togglePageSearch () {
      this.pageSearchOpen = !this.pageSearchOpen
      if (this.pageSearchOpen) {
        this.$nextTick(() => {
          const ti = this.$refs.pageSearchInput
          if (ti) ti.focus()
        })
      } else {
        this.closePageSearch()
      }
    },

    closePageSearch () {
      clearTimeout(this.pageSearchDebounceId)
      clearTimeout(this._pageSearchChunkTimer)
      this.clearPageSearchHighlight()
      this.pageSearchOpen = false
      this._pageSearchTypedQuery = ''
      this.pageSearchQuery = ''
      this.pageSearchMatchIndex = 0
      this.pageSearchMatchCount = 0
      this.pageSearchDebounceId = null
      this.pageSearchMatches = []
      this.pageSearchSearching = false
      this._pageSearchRunId += 1

      const ti = this.$refs.pageSearchInput
      if (ti && ti.$refs && ti.$refs.input) ti.$refs.input.value = ''
    },

    clearPageSearchHighlight () {
      if (this._pageSearchHighlightEl) {
        this._pageSearchHighlightEl.classList.remove('billing-page-search-match', 'billing-page-search-match--current')
        this._pageSearchHighlightEl = null
      }
    },

    normalizePageSearchText (parts) {
      return parts
        .filter(part => part != null && part !== '')
        .map(part => String(part).toLowerCase())
        .join(' ')
    },

    rebuildPageSearchIndex () {
      const index = []
      if (!this.bill) {
        this.pageSearchIndex = index
        return
      }

      const formFields = [
        ['account', this.current.account?.name],
        ['system', this.current.account?.system?.name],
        ['dueDate', this.bill.dueDate ? this.$formatUTCDate(this.bill.dueDate) : ''],
        ['billableCount', this.bill.totals?.count?.active],
        ['holdingPausedCount', (this.bill.totals?.count?.holding ?? 0) + (this.bill.totals?.count?.paused ?? 0)],
        ['billableTotal', String(this.bill.totals?.total?.active ?? '') + ' ' + String(this.current.currency ?? '')]
      ]

      formFields.forEach(([key, value]) => {
        index.push({
          type: 'form',
          key,
          text: this.normalizePageSearchText([value])
        })
      })

      this.bill.groups.forEach((group, groupIndex) => {
        const description = group.rate?.description
        if (description && description !== 'Default') {
          index.push({
            type: 'section',
            groupIndex,
            text: this.normalizePageSearchText([description])
          })
        }

        index.push({
          type: 'total',
          groupIndex,
          text: this.normalizePageSearchText([
            group.totals.count.count,
            group.totals.count.active,
            group.totals.count.holding,
            group.totals.count.paused,
            group.totals.total.active,
            this.current.currency,
            group.cycle.cycle
          ])
        })

        group.devices.forEach((device) => {
          index.push({
            type: 'device',
            groupIndex,
            deviceId: device.id,
            text: this.normalizePageSearchText([
              device.id,
              device.name,
              device.type,
              device.fleet,
              this.carriersMap[device.carrier],
              this.activationStatusMap[device.activation.status],
              group.rate.rate,
              this.current.currency
            ])
          })
        })
      })

      this.pageSearchIndex = index
    },

    findPageSearchMatches (q) {
      if (!this.pageSearchIndex.length) return []
      return this.pageSearchIndex
        .filter(entry => entry.text.includes(q))
        .map(({ text, ...match }) => match)
    },

    runPageSearchInChunks (q, runId) {
      const matches = []
      const batchSize = 150
      let cursor = 0

      const scanBatch = () => {
        if (runId !== this._pageSearchRunId) return

        const end = Math.min(cursor + batchSize, this.pageSearchIndex.length)
        for (let i = cursor; i < end; i++) {
          const entry = this.pageSearchIndex[i]
          if (entry.text.includes(q)) {
            const { text, ...match } = entry
            matches.push(match)
          }
        }
        cursor = end

        if (cursor < this.pageSearchIndex.length) {
          this._pageSearchChunkTimer = setTimeout(scanBatch, 0)
          return
        }

        if (runId !== this._pageSearchRunId) return

        this.pageSearchMatches = matches
        this.pageSearchMatchCount = matches.length
        if (this.pageSearchMatchIndex >= matches.length) this.pageSearchMatchIndex = 0
        this.pageSearchSearching = false

        this.$nextTick(() => {
          if (runId !== this._pageSearchRunId) return
          this.applyCurrentPageSearchHighlight()
        })
      }

      scanBatch()
    },

    getPageSearchMatchElement (match) {
      const root = this.$refs.billingPageSearchRoot
      if (!root || !match) return null

      if (match.type === 'form') {
        return root.querySelector('[data-page-search-form="' + match.key + '"]')
      }

      if (match.type === 'section') {
        return root.querySelector('[data-page-search-section="' + match.groupIndex + '"]')
      }

      if (match.type === 'total') {
        return root.querySelector('[data-page-search-total="' + match.groupIndex + '"]')
      }

      if (match.type === 'device') {
        return root.querySelector('[data-page-search-device="' + match.groupIndex + ':' + match.deviceId + '"]')
      }

      return null
    },

    applyCurrentPageSearchHighlight () {
      this.clearPageSearchHighlight()
      const match = this.pageSearchMatches[this.pageSearchMatchIndex]
      if (!match) return

      const el = this.getPageSearchMatchElement(match)
      if (!el) return

      el.classList.add('billing-page-search-match', 'billing-page-search-match--current')
      this._pageSearchHighlightEl = el
      el.scrollIntoView({ block: 'center', behavior: 'smooth' })
    },

    onPageSearchKeydown (e) {
      if (e.key !== 'Enter') return
      e.preventDefault()

      if (this.pageSearchQuery !== this._pageSearchTypedQuery) {
        this.pageSearchMatchIndex = 0
        this.pageSearchQuery = this._pageSearchTypedQuery
        this.runPageSearch()
        return
      }

      this.pageSearchStep(e.shiftKey ? -1 : 1)
    },

    onPageSearchInput (e) {
      this._pageSearchTypedQuery = e.target.value
      this.pageSearchMatchIndex = 0
      this.queuePageSearch()
    },

    queuePageSearch () {
      clearTimeout(this.pageSearchDebounceId)
      this.pageSearchDebounceId = setTimeout(() => {
        this.pageSearchQuery = this._pageSearchTypedQuery
        this.runPageSearch()
      }, 450)
    },

    runPageSearch () {
      clearTimeout(this._pageSearchChunkTimer)
      const runId = ++this._pageSearchRunId
      const q = this.pageSearchQuery.trim().toLowerCase()

      this.clearPageSearchHighlight()
      this.pageSearchSearching = false

      if (!q) {
        this.pageSearchMatches = []
        this.pageSearchMatchCount = 0
        this.pageSearchMatchIndex = 0
        return
      }

      this.pageSearchSearching = true
      this.runPageSearchInChunks(q, runId)
    },

    pageSearchStep (delta) {
      const matches = this.pageSearchMatches
      if (!matches.length) return
      this.pageSearchMatchIndex = (this.pageSearchMatchIndex + delta + matches.length) % matches.length
      this.applyCurrentPageSearchHighlight()
    },

    exportEmailElement () {
      return this.$refs.exportEmailInput?.$refs?.input || null
    },

    resetExportEmail () {
      this._exportEmail = ''
      if (this.exportEmailError !== null) {
        this.exportEmailError = null
      }
      const input = this.exportEmailElement()
      if (input) {
        input.value = ''
      }
    },

    onExportEmailInput (e) {
      this._exportEmail = e.target.value
      if (this.exportEmailError !== null) {
        this.exportEmailError = null
      }
    },

    exportCsv () {
      if (this.exportLoading) return

      this.resetExportEmail()
      this.$refs.exportModal.open()
      this.$nextTick(() => {
        const input = this.exportEmailElement()
        if (input) {
          input.value = ''
          input.focus()
        }
      })
    },

    closeExportModal () {
      this.resetExportEmail()
      this.$refs.exportModal.close()
    },

    requestManualExport () {
      if (this.exportLoading) return

      const email = String(this._exportEmail || '').trim()
      if (!email) {
        this.exportEmailError = 'E-mail is required'
        return
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.exportEmailError = 'Enter a valid e-mail'
        return
      }

      this.exportEmailError = null
      this.exportLoading = true

      const dueDate = this.$route.params.dueDate || this.bill?.dueDate
      const { systemId, accountId } = this.$route.params

      this.$apollo.query({
        query: gql`
          query ($systemId: String!, $monthOfReference: String!, $emails: [String]!, $accountId: ID) {
            billing {
              requestAllAccountsBillingReport(
                systemId: $systemId
                monthOfReference: $monthOfReference
                emails: $emails
                accountId: $accountId
              )
            }
          }
        `,
        variables: {
          systemId,
          accountId,
          monthOfReference: String(dueDate).substring(0, 10),
          emails: [email]
        },
        fetchPolicy: 'no-cache'
      }).then(() => {
        this.closeExportModal()
        this.$messageBox('Report requested. You will receive an e-mail when it is ready.', 'success', { timeout: 6 })
      }).catch((error) => {
        const message = error?.graphQLErrors?.[0]?.message || error?.message || 'Failed to request the report.'
        this.$messageBox(message, 'danger')
      }).finally(() => {
        this.exportLoading = false
      })
    },

    extractMonthOfReference (date) {
      const dt = new Date(date)
      return dt.toLocaleString(undefined, { month: 'numeric', year: 'numeric' })
    }
  },

  computed: {
    bill () {
      const normal = this.current?.rates?.normal
      if (!Array.isArray(normal)) return null
      return normal.find(i => i.dueDate === this.$route.params.dueDate) || null
    }
  },

  watch: {
    bill () {
      this.$nextTick(() => this.rebuildPageSearchIndex())
    },

    loading (v) {
      if (!v) {
        this.$nextTick(() => {
          this.rebuildPageSearchIndex()
          if (this.pageSearchQuery.trim()) this.queuePageSearch()
        })
      }
    }
  },

  data () {
    return {
      current: {},
      loading: true,
      exportLoading: false,
      exportEmailError: null,

      pageSearchOpen: false,
      pageSearchQuery: '',
      pageSearchMatchIndex: 0,
      pageSearchMatchCount: 0,
      pageSearchSearching: false,
      pageSearchDebounceId: null,
      pageSearchMatches: [],
      pageSearchIndex: [],
      carriersMap: {
        at_t: 'AT&T',
        'kore-us': 'Kore (North America)',
        'kore-au': 'Kore (Australia)',
        'kore-eu': 'Kore (Europe/South America)'
      },
      activationStatusMap: {
        active: 'Active',
        holding: 'Installation Holding',
        paused: 'Paused'
      }
    }
  },

  created () {
    this._pageSearchRunId = 0
    this._pageSearchHighlightEl = null
    this._pageSearchChunkTimer = null
    this._pageSearchTypedQuery = ''
    this._exportEmail = ''
  },

  mounted () {
    this.getBill()
    this._onPageSearchEsc = (e) => {
      if (e.key === 'Escape' && this.pageSearchOpen) this.closePageSearch()
    }
    document.addEventListener('keydown', this._onPageSearchEsc)
  },

  beforeDestroy () {
    document.removeEventListener('keydown', this._onPageSearchEsc)
    clearTimeout(this.pageSearchDebounceId)
    clearTimeout(this._pageSearchChunkTimer)
  }
}
</script>

<style lang="less" scoped>
@import '../../assets/shared.less';

.export-modal-body {
  min-width: 360px;
  max-width: 420px;
}

.export-modal-hint {
  margin: 0 0 12px;
  opacity: 0.7;
  font-size: 10pt;
}

.billing-report-detail-controls {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: flex-end;
  gap: 8px;
}

.billing-page-search-controls {
  display: inline-flex;
  align-items: flex-start;
  gap: 6px;
}

/deep/ .billing-page-search-toggle i,
/deep/ .billing-page-search-nav i,
/deep/ .billing-page-search-close i {
  display: inline-block !important;
}

.billing-page-search-toggle {
  border: none;
  background: transparent;
  color: @accent-color;
  cursor: pointer;
  padding: 8px 10px;
  font-size: 16pt;
  line-height: 1;
}

.billing-page-search-expand {
  display: flex;
  max-width: 0;
  overflow: hidden;
  opacity: 0;
  transform: translateX(8px);
  pointer-events: none;
  transition: max-width 0.32s cubic-bezier(0.4, 0, 0.2, 1),
              opacity 0.24s ease,
              transform 0.32s cubic-bezier(0.4, 0, 0.2, 1);

  &.is-open {
    max-width: 360px;
    opacity: 1;
    transform: translateX(0);
    pointer-events: auto;
  }
}

.billing-page-search-expand-inner {
  flex: 0 0 auto;
  min-width: 0;
}

.billing-page-search-field-wrap {
  display: flex;
  align-items: flex-end;
  gap: 4px;
  min-width: 240px;
  max-width: 320px;
}

.billing-page-search-input {
  flex: 1;
}

.billing-page-search-close {
  border: none;
  background: transparent;
  color: fade(contrast(@main-color), 45%);
  cursor: pointer;
  padding: 6px 8px;
  font-size: 14pt;
  line-height: 1;
  border-radius: 6px;
  transition: color 150ms ease, background-color 150ms ease;

  &:hover {
    color: @accent-color;
    background: fade(@accent-color, 10%);
  }
}

.billing-page-search-meta {
  margin-top: 4px;
  font-size: 11pt;
  opacity: 0.75;
  display: flex;
  align-items: center;
  gap: 6px;
}

.billing-page-search-nav {
  border: none;
  background: fade(@accent-color, 12%);
  color: @accent-color;
  border-radius: 4px;
  padding: 2px 8px;
  cursor: pointer;
}

/deep/ .billing-page-search-match {
  box-shadow: 0 0 0 2px fade(@accent-color, 35%);
  border-radius: 4px;
}

/deep/ .billing-page-search-match--current {
  box-shadow: 0 0 0 2px @accent-color;
}

/deep/ td.billing-page-search-match,
/deep/ .form-item.billing-page-search-match,
/deep/ .billing-section-title.billing-page-search-match,
/deep/ .total.billing-page-search-match {
  background-color: fade(@accent-color, 10%);
  box-shadow: inset 0 0 0 2px fade(@accent-color, 45%);
}

/deep/ td.billing-page-search-match--current,
/deep/ .form-item.billing-page-search-match--current,
/deep/ .billing-section-title.billing-page-search-match--current,
/deep/ .total.billing-page-search-match--current {
  background-color: fade(@accent-color, 16%);
  box-shadow: inset 0 0 0 2px @accent-color;
}

/deep/ .input-list-holder .item.billing-page-search-match {
  box-shadow: 0 0 0 2px fade(contrast(@accent-color), 55%);
}

/deep/ .input-list-holder .item.billing-page-search-match--current {
  box-shadow: 0 0 0 2px contrast(@accent-color),
              0 0 0 5px fade(contrast(@accent-color), 35%);
  position: relative;
  z-index: 1;
}

.form {
  padding-bottom: 15px;
  border-bottom: 1px dotted fade(contrast(@main-color), 10%);
  margin-bottom: 15px;
}

.billing-section {
  margin-bottom: 30px;

  .billing-section-title {
    font-size: 18pt;
    opacity: 0.5;

    &.static {
      font-weight: bold;
      text-transform: uppercase;
    }
  }

  .total {
    text-align: right;
    font-size: 14pt;
    margin-top: 10px;

    strong {
      margin-left: 10px;
    }

    .sep {
      &::before {
        content: '//';
      }

      margin: 0 20px;
      color: contrast(@main-color);
      opacity: 0.3;
    }
  }
}

.badge {
  background: fade(@success-color, 10%);
  color: @success-color;
  padding: 5px 12px;
  border-radius: 3px;
  font-weight: bold;

  &.holding, &.paused {
    background: fade(@warning-color, 10%);
    color: @warning-color;
  }
}

.rate.holding, .rate.paused {
  text-decoration: line-through;
  opacity: 0.5;
}
</style>
